/**
 * The per-app sub-navigation. Renders as a left-side vertical nav that
 * sits beside each /apps/:slug/* page's content. Pages compose it
 * into a `grid grid-cols-12` row whose other child is the page's
 * `<main className="col-span-...">` content column.
 *
 * Was previously a horizontal tab bar; moved to a side nav so the
 * tab list (which now runs to 10+ items) is easier to scan and so
 * the active route gets a more prominent affordance than a thin
 * underline.
 */

import { NavLink } from 'react-router-dom';
import {
  IconActivity,
  IconBolt,
  IconBot,
  IconHeart,
  IconHistory,
  IconKey,
  IconClock,
  IconLink,
  IconPlug,
  IconSettings,
  IconShield,
  IconSparkle,
  IconUsers,
  IconInfo,
} from './Icons.js';

interface AppTabsProps {
  slug: string;
  /**
   * The caller's manager status on this app. Every deeper surface is
   * manager-only — non-managers see a single simplified overview at
   * /apps/:slug with no tab nav. Omit / leave undefined while the app
   * row is still loading — better to render the full tab set briefly
   * than to flash tabs in and then out.
   */
  isManager?: boolean;
  /**
   * Whether the app has any registered schedules. Used to hide the
   * Schedules tab on apps that have never had one — the page would
   * otherwise render an empty list with no way to add anything (the
   * MCP tool is the only creator). Default false so a missing flag
   * is treated as "no schedules" rather than accidentally surfacing
   * a dead tab.
   */
  hasSchedules?: boolean;
}

interface Tab {
  to: string;
  label: string;
  icon: typeof IconHeart;
  end?: boolean;
  managerOnly?: boolean;
  /**
   * Conditional visibility key. When `null` the tab is unconditional;
   * otherwise the tab only renders when the named flag is true.
   */
  requires?: 'hasSchedules';
}

/**
 * Order matters — this is the visible nav order from top to bottom.
 * Grouping by intent: overview at the top; usage + access + tokens
 * (the who), then versions + editor + secrets (the code), then health
 * + activity (the ops), then Settings at the bottom. Schedules is
 * intentionally NOT always in the nav — it only surfaces when the app
 * actually has at least one registered.
 */
const tabs: (slug: string) => Tab[] = (slug) => [
  { to: `/apps/${slug}`, label: 'Overview', icon: IconInfo, end: true },
  { to: `/apps/${slug}/usage`, label: 'Usage', icon: IconBolt, managerOnly: true },
  { to: `/apps/${slug}/share`, label: 'Access', icon: IconUsers, managerOnly: true },
  { to: `/apps/${slug}/tokens`, label: 'Tokens', icon: IconShield, managerOnly: true },
  { to: `/apps/${slug}/versions`, label: 'Versions', icon: IconHistory, managerOnly: true },
  { to: `/apps/${slug}/agent`, label: 'Agent', icon: IconBot, managerOnly: true },
  { to: `/apps/${slug}/editor`, label: 'File Editor', icon: IconSparkle, managerOnly: true },
  { to: `/apps/${slug}/secrets`, label: 'Secrets', icon: IconKey, managerOnly: true },
  { to: `/apps/${slug}/connections`, label: 'Connections', icon: IconPlug, managerOnly: true },
  {
    to: `/apps/${slug}/dependencies`,
    label: 'Dependencies',
    icon: IconLink,
    managerOnly: true,
  },
  { to: `/apps/${slug}/health`, label: 'Health', icon: IconHeart, managerOnly: true },
  { to: `/apps/${slug}/activity`, label: 'Activity', icon: IconActivity, managerOnly: true },
  {
    to: `/apps/${slug}/schedules`,
    label: 'Schedules',
    icon: IconClock,
    managerOnly: true,
    requires: 'hasSchedules',
  },
  { to: `/apps/${slug}/settings`, label: 'Settings', icon: IconSettings, managerOnly: true },
];

export function AppTabs({ slug, isManager, hasSchedules = false }: AppTabsProps) {
  const visibleTabs = tabs(slug).filter((t) => {
    if (t.managerOnly && isManager === false) return false;
    if (t.requires === 'hasSchedules' && !hasSchedules) return false;
    return true;
  });
  // Non-managers see only the Overview tab. Rendering a one-item nav
  // adds visual noise without offering navigation, so collapse to
  // nothing when the only surviving tab is Overview itself.
  if (visibleTabs.length <= 1) return null;
  return (
    <aside
      aria-label="App sections"
      className="col-span-12 lg:col-span-3 xl:col-span-2 lg:sticky lg:top-4 lg:self-start"
    >
      <nav className="flex flex-col gap-0.5">
        {visibleTabs.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            {...(end ? { end: true } : {})}
            className={({ isActive }) =>
              'group relative flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 ' +
              (isActive
                ? 'text-ink bg-surface-2'
                : 'text-muted hover:text-ink hover:bg-surface-2/60')
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-brand rounded-full"
                  />
                )}
                <Icon
                  size={14}
                  className={isActive ? 'text-brand' : 'text-faint group-hover:text-muted'}
                />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
